import { memo, useEffect, useRef } from 'react';

const TRAIL_POINT_COUNT = 60;

function MouseTrail() {
  const linesRef = useRef([]);

  useEffect(() => {
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (coarsePointer) return undefined;

    const pointCount = TRAIL_POINT_COUNT;
    const ease = 0.75;
    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const points = Array.from({ length: pointCount }, () => ({ x: -15, y: -15 }));
    let frameId = 0;
    let running = false;
    let initialized = false;
    let disposed = false;

    const requestTick = () => {
      if (disposed || running || document.hidden) return;
      running = true;
      frameId = requestAnimationFrame(animate);
    };

    const updateTarget = (x, y) => {
      target.x = x;
      target.y = y;
      if (!initialized) {
        points.forEach((point) => {
          point.x = x;
          point.y = y;
        });
        initialized = true;
      }
      requestTick();
    };

    const setTarget = (event) => updateTarget(event.clientX, event.clientY);

    const setTargetFromFrame = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'appivorous-pointer') return;
      updateTarget(Number(event.data.x) || 0, Number(event.data.y) || 0);
    };

    const animate = () => {
      if (disposed || document.hidden) {
        running = false;
        frameId = 0;
        return;
      }

      let leaderX = target.x;
      let leaderY = target.y;
      let remainingMotion = 0;

      points.forEach((point, index) => {
        const dx = leaderX - point.x;
        const dy = leaderY - point.y;
        point.x += dx * ease;
        point.y += dy * ease;
        remainingMotion += Math.abs(dx) + Math.abs(dy);

        const line = linesRef.current[index];
        if (line) {
          line.setAttribute('x1', point.x);
          line.setAttribute('y1', point.y);
          line.setAttribute('x2', leaderX);
          line.setAttribute('y2', leaderY);
        }

        leaderX = point.x;
        leaderY = point.y;
      });

      if (remainingMotion > 0.75 && !document.hidden) {
        frameId = requestAnimationFrame(animate);
      } else {
        running = false;
        frameId = 0;
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) return;
      cancelAnimationFrame(frameId);
      frameId = 0;
      running = false;
    };

    window.addEventListener('mousemove', setTarget, { passive: true });
    window.addEventListener('message', setTargetFromFrame);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      window.removeEventListener('mousemove', setTarget);
      window.removeEventListener('message', setTargetFromFrame);
      document.removeEventListener('visibilitychange', handleVisibility);
      cancelAnimationFrame(frameId);
      frameId = 0;
      running = false;
    };
  }, []);

  return (
    <div className="mouse-trail" aria-hidden="true">
      <svg>
        {Array.from({ length: TRAIL_POINT_COUNT }, (_, index) => (
          <line
            key={index}
            ref={(node) => { linesRef.current[index] = node; }}
            opacity={(TRAIL_POINT_COUNT - index) / TRAIL_POINT_COUNT}
          />
        ))}
      </svg>
    </div>
  );
}

export default memo(MouseTrail);
